// packages/main/app/web/components-v2/UnifiedImageDialog/AIGenerateTab.tsx
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { useEffect } from 'react';
import { useMultipleAttachmentsQuery } from '../../../../queries/Attachments/useMultipleAttachmentsQuery';
import { AIStarsIcon } from '../../../components/svgs/icons/AIStars';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { THEME } from '../../Theme/theme';
import { ImageRow } from './components/ImageRow';
import { useApplyBrandingToImages } from './hooks/applyBranding';
import { styleSheet } from './styles';

interface BulkRebrandingTabProps {
	impersonationContext?: Api.IImpersonationContext;
	bulkRebrandingImages?: Api.IFileAttachment[];
	onBulkRebrandingImagesSelected?: (attachments: Api.IFileAttachment[]) => void;
	onOpenChange: (open: boolean) => void;
	templateId?: string;
}

export function BulkRebrandingTab({
	bulkRebrandingImages,
	impersonationContext,
	onBulkRebrandingImagesSelected,
	onOpenChange,
	templateId,
}: BulkRebrandingTabProps) {
	const [selectedImages, setSelectedImages] = React.useState<Api.IFileAttachment[]>([]);
	// A picture picked twice sits in the media twice. It is branded once, and every copy takes the result.
	const distinctImages = React.useMemo(
		() => [...new Map((bulkRebrandingImages ?? []).map(image => [image.id, image])).values()],
		[bulkRebrandingImages]
	);

	useEffect(() => {
		setSelectedImages(distinctImages);
	}, [distinctImages]);

	const handleImageSelected = (image: Api.IFileAttachment) => {
		setSelectedImages(prev => {
			if (prev.includes(image)) {
				return prev.filter(i => i.id !== image.id);
			}
			return [...prev, image];
		});
	};

	const {
		applyBranding,
		isGenerating,
		resultImages,
		failedImages,
		totalImages,
		completedImages,
		currentPhase,
		currentImageNumber,
		currentImageAttempt,
		systemJobStatus,
	} = useApplyBrandingToImages({
		impersonationContext,
		templateId,
	});

	const progressText = React.useMemo(() => {
		// Before the background job starts, show a neutral "Starting…" rather than claiming "image 1 of N".
		if (!currentPhase && systemJobStatus !== 'Processing') {
			return 'Starting…';
		}
		if (totalImages <= 0) {
			return '';
		}
		if (currentPhase) {
			const imageNumber = Math.min(currentImageNumber ?? 1, totalImages);
			const attempt = currentImageAttempt ?? 1;
			const verb = currentPhase === 'Reviewing' ? 'Reviewing' : attempt > 1 ? 'Refining' : 'Generating';
			const attemptSuffix = attempt > 1 ? ` (attempt ${attempt})` : '';
			return `${verb} image ${imageNumber} of ${totalImages}…${attemptSuffix}`;
		}
		return `Branding image ${Math.min(completedImages + 1, totalImages)} of ${totalImages}…`;
	}, [completedImages, currentImageAttempt, currentImageNumber, currentPhase, systemJobStatus, totalImages]);

	const [selectedResultImages, setSelectedResultImages] = React.useState<
		{
			fileAttachmentId: string;
			mimeType: string;
			sourceFileAttachmentId: string;
		}[]
	>([]);

	const imagesToGenerate = React.useMemo(() => {
		return distinctImages.filter(image => !selectedResultImages.some(r => r.sourceFileAttachmentId === image.id));
	}, [distinctImages, selectedResultImages]);

	const generatedAttachmentsIds = React.useMemo(() => {
		return selectedResultImages.map(image => image.fileAttachmentId);
	}, [selectedResultImages]);

	const generatedAttachmentsQuery = useMultipleAttachmentsQuery({
		ids: generatedAttachmentsIds,
		enabled: generatedAttachmentsIds.length > 0,
		impersonationContext,
	});

	const brandedAttachments = React.useMemo(() => {
		const attachments = generatedAttachmentsQuery
			.filter(query => query.isSuccess)
			.flatMap(query => query.data ?? ([] as Api.IFileAttachment[]));

		// Deduplicate by attachment ID to prevent duplicates
		const uniqueAttachments = new Map<string, Api.IFileAttachment>();
		attachments.forEach(attachment => {
			if (attachment?.id) {
				uniqueAttachments.set(attachment.id, attachment);
			}
		});

		return Array.from(uniqueAttachments.values());
	}, [generatedAttachmentsQuery]);

	const isLoadingAttachments = React.useMemo(() => {
		return generatedAttachmentsQuery.some(query => query.isLoading);
	}, [generatedAttachmentsQuery]);

	// Each branded image takes its original's place, every copy of it, so saving never reorders the media.
	const getMediaWithBranding = () =>
		(bulkRebrandingImages ?? []).map(image => {
			const result = selectedResultImages.find(r => r.sourceFileAttachmentId === image.id);
			return (result && brandedAttachments.find(b => b.id === result.fileAttachmentId)) ?? image;
		});

	const handleApplyBranding = () => {
		const attachmentIds = selectedImages
			.filter(image => !selectedResultImages.some(r => r.sourceFileAttachmentId === image.id))
			.map(image => image.id);

		applyBranding({ attachmentIds });
	};

	useEffect(() => {
		if (resultImages && resultImages.length > 0) {
			setSelectedResultImages(prev => {
				// Create a map of existing results by sourceFileAttachmentId for quick lookup
				const existingMap = new Map(prev.map(r => [r.sourceFileAttachmentId, r]));

				// Add/update with new results
				resultImages.forEach(newResult => {
					existingMap.set(newResult.sourceFileAttachmentId, newResult);
				});

				return Array.from(existingMap.values());
			});
		}
	}, [resultImages]);

	return (
		<div className={css(styleSheet.bulkRebrandingContainer)}>
			<div className={css(styleSheet.bulkRebrandingHeader)}>
				<h4>Apply my Branding</h4>
			</div>

			<div className={css(styleSheet.imageSelectionContainer)}>
				<div className={css(styleSheet.brandingActionRow)}>
					<Button
						size='sm'
						variant='secondary'
						onClick={() => handleApplyBranding()}
						disabled={imagesToGenerate.length === 0 || isGenerating}
						isLoading={isGenerating}
					>
						<AIStarsIcon stopColorOne={THEME.colors.primary.main} stopColorTwo={THEME.colors.primary.main} />
						Apply my Branding
					</Button>

					{isGenerating && progressText && (
						<span role='status' aria-live='polite' className={css(styleSheet.brandingProgressStatus)}>
							{progressText}
						</span>
					)}
				</div>

				{failedImages > 0 && (
					<span className={css(styleSheet.failedImagesText)}>
						{failedImages} {failedImages === 1 ? 'image' : 'images'} failed, you can try again by clicking the button
						above
					</span>
				)}

				<div className={css(styleSheet.imagesSelectionList)}>
					{imagesToGenerate.map(image => (
						<div key={image.id} className={css(styleSheet.imageSelectionItem)}>
							<img src={image.url} alt={image.fileName} className={css(styleSheet.imageSelectionItemImage)} />

							<label className={css(styleSheet.checkboxLabel)}>
								<Checkbox
									checked={selectedImages.includes(image)}
									onCheckedChange={() => handleImageSelected(image)}
									disabled={isGenerating || isLoadingAttachments}
								/>
								Select Image
							</label>
						</div>
					))}
				</div>
			</div>

			<div className={css(styleSheet.resultContainer)}>
				{selectedResultImages.map(image => (
					<div key={image.fileAttachmentId}>
						<ImageRow
							selected={selectedImages.includes(image)}
							setSelected={handleImageSelected}
							originalAttachment={bulkRebrandingImages?.find(b => b?.id === image.sourceFileAttachmentId)}
							brandedAttachment={brandedAttachments?.find(b => b?.id === image.fileAttachmentId)}
							onCancel={() =>
								setSelectedResultImages(prev => prev.filter(i => i.fileAttachmentId !== image.fileAttachmentId))
							}
						/>
					</div>
				))}
			</div>

			<div className={css(styleSheet.modalActionsContainer)}>
				<Button
					variant='primary'
					size='sm'
					onClick={() => onBulkRebrandingImagesSelected?.(getMediaWithBranding())}
					disabled={isLoadingAttachments || isGenerating}
				>
					Save
				</Button>

				<Button variant='secondary' size='sm' onClick={() => onOpenChange(false)}>
					Cancel
				</Button>
			</div>
		</div>
	);
}
